import React, { useState, useEffect } from 'react';
import {
  FaSpinner,
  FaExclamationTriangle,
  FaArrowLeft,
  FaMagic,
  FaCheckCircle,
} from 'react-icons/fa';
import { getConfiguredItinerary, cloneConfiguredItinerary } from '../api/client';
import ItineraryDisplay from './ItineraryDisplay';
import { useToast, ToastContainer } from './Toast';

/**
 * ConfiguredItineraryDetail — view a pre-built configured itinerary.
 *
 * Loads the template by slug and renders it with the real ItineraryDisplay
 * component in read-only mode (isOwner=false). Adds a top banner with a
 * "Use this itinerary" button that clones the template into the user's
 * own account and lands them on the editable copy.
 */
const ConfiguredItineraryDetail = ({ slug, user, onBack, onCloned, onViewPricing }) => {
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cloning, setCloning] = useState(false);
  const { toasts, removeToast, showSuccess, showError, showUpgrade } = useToast();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await getConfiguredItinerary(slug);
        if (!cancelled) setItinerary(data);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Failed to load this itinerary.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleClone = async () => {
    if (cloning) return;
    setCloning(true);
    try {
      const result = await cloneConfiguredItinerary(slug);
      showSuccess('Itinerary copied to your account! Loading…');
      if (onCloned && result?.request_id) {
        setTimeout(() => onCloned(result.request_id), 600);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'trip_limit_reached') {
        showUpgrade(detail.message || 'You have reached your trip limit.', {
          action: {
            label: 'Upgrade Now',
            onClick: () => onViewPricing && onViewPricing(),
          },
        });
      } else if (typeof detail === 'string') {
        showError(detail);
      } else {
        showError('Could not clone this itinerary. Please try again.');
      }
      setCloning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-neutral-500">
        <FaSpinner className="text-4xl text-primary-500 animate-spin mb-3" />
        <p className="text-sm">Loading configured itinerary…</p>
      </div>
    );
  }

  if (error || !itinerary) {
    return (
      <div className="max-w-3xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-neutral-700 hover:text-primary-600 font-medium mb-4 transition-colors"
        >
          <FaArrowLeft />
          <span>Back to Configured Itineraries</span>
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3">
          <FaExclamationTriangle className="text-red-500 text-xl flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-semibold">Couldn't load this itinerary</p>
            <p className="text-red-600 text-sm mt-1">{error || 'Unknown error.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Back link */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-neutral-700 hover:text-primary-600 font-medium mb-4 transition-colors"
      >
        <FaArrowLeft />
        <span>Back to Configured Itineraries</span>
      </button>

      {/* Clone banner */}
      <div className="bg-gradient-to-r from-purple-500 via-primary-500 to-secondary-500 rounded-2xl p-5 md:p-6 mb-6 text-white shadow-elevated">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <FaMagic className="text-2xl flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg md:text-xl font-bold mb-1">
                Like this itinerary? Make it yours.
              </h2>
              <p className="text-white/90 text-sm md:text-base">
                Clone it to your account in one click — then customize dates, stops, packing list, anything you want.
              </p>
            </div>
          </div>
          <button
            onClick={handleClone}
            disabled={cloning}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all whitespace-nowrap shadow-md ${
              cloning
                ? 'bg-white/30 text-white cursor-not-allowed'
                : 'bg-white text-primary-600 hover:bg-neutral-50 hover:shadow-lg'
            }`}
          >
            {cloning ? (
              <>
                <FaSpinner className="animate-spin" />
                Copying…
              </>
            ) : (
              <>
                <FaCheckCircle />
                Use this itinerary
              </>
            )}
          </button>
        </div>
      </div>

      {/* Read-only itinerary view — uses the SAME ItineraryDisplay component. */}
      <ItineraryDisplay
        itinerary={itinerary}
        onReset={onBack}
        onRegenerate={() => {}}
        onChatModificationApplied={() => {}}
        onRefresh={() => {}}
        onRefreshItinerary={() => {}}
        isOwner={false}
        isMember={false}
        hideModify
        headerTitle="Curated Itinerary"
        headerSubtitle={
          itinerary?.start_location && itinerary?.end_location
            ? `${itinerary.start_location} → ${itinerary.end_location}`
            : 'Read-only preview — clone above to make it yours'
        }
        onViewPricing={onViewPricing}
        currentUser={user}
      />
    </div>
  );
};

export default ConfiguredItineraryDetail;
